import { styled } from "@mui/material"
import Typography from "@mui/material/Typography"
import ScheduleMeetingDialogCloseIconButton from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogCloseIconButton"
import ScheduleMeetingDialogContainer from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogContainer"
import ScheduleMeetingDialogContent from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogContent"
import ScheduleMeetingDialogHeader from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogHeader"
import { SelectMeetingType } from "components/meetings/SelectMeetingType"
import attrs from "models/convention-api/attrs"
import ConventionMeetingAvailability from "models/convention-api/v1/ConventionMeetingAvailability"
import ConventionMeetingType from "models/convention-api/v1/ConventionMeetingType"
import moment from "moment-timezone"
import React, { useMemo } from "react"
import { SWRResponse } from "swr"

const momentAttr = attrs.moment()

interface ScheduleMeetingDialogSelectTypeProps {
  onClose: () => void
  onSelectMeetingType: (selectedMeetingType: ConventionMeetingType) => void
  meetingTypes: SWRResponse<ConventionMeetingType[], unknown>
  meetingAvailabilities: SWRResponse<ConventionMeetingAvailability[], unknown>
  institutionName: string
}

const ScheduleMeetingDialogSelectType = (props: ScheduleMeetingDialogSelectTypeProps) => {
  const { onClose, onSelectMeetingType, meetingTypes, institutionName, meetingAvailabilities } =
    props

  const isLoading =
    (meetingTypes.data === undefined && meetingTypes.isValidating) ||
    (meetingAvailabilities.data === undefined && meetingAvailabilities.isValidating)

  const meetingTypesWithAvailableSlots = useMemo(() => {
    if (!meetingTypes.data) return []
    if (!meetingAvailabilities.data) return []

    const minAvailableTime = moment().add(15, "minutes")
    const shouldShowMeetingType: Record<string, boolean> = {}

    meetingAvailabilities.data.forEach((conventionMeetingAvailability) => {
      if (shouldShowMeetingType[conventionMeetingAvailability.convention_meeting_type_id]) return
      const availableSlots = conventionMeetingAvailability.available_times ?? []

      // find out if it has at least one available slot
      const foundAvailableSlot = !!availableSlots.find(([startAt]) => {
        return momentAttr.deserialize(startAt).isAfter(minAvailableTime)
      })

      if (foundAvailableSlot)
        shouldShowMeetingType[conventionMeetingAvailability.convention_meeting_type_id] = true
    })

    return meetingTypes.data.filter((meetingType) => shouldShowMeetingType[meetingType.id])
  }, [meetingTypes.data, meetingAvailabilities.data])

  return (
    <>
      <ScheduleMeetingDialogHeader>
        <_AccentText component="h3">Select a meeting type</_AccentText>
        <ScheduleMeetingDialogCloseIconButton onClick={onClose} />
      </ScheduleMeetingDialogHeader>
      <ScheduleMeetingDialogContainer
        isLoading={isLoading}
        hasNoData={meetingTypesWithAvailableSlots.length === 0}
        noDataText={`${institutionName} doesn't have any available dates.`}>
        <ScheduleMeetingDialogContent sx={{ marginTop: "30px" }}>
          <SelectMeetingType
            meetingTypes={meetingTypesWithAvailableSlots}
            onSelect={onSelectMeetingType}
          />
        </ScheduleMeetingDialogContent>
      </ScheduleMeetingDialogContainer>
    </>
  )
}

export default ScheduleMeetingDialogSelectType

const _AccentText = styled(Typography)(({ theme }) => ({
  ...theme.fonts.lato400,
  fontSize: 20,
  lineHeight: 1.2,
})) as typeof Typography
